import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/useToast';
import {
  useAdSchedulesQuery,
  useCreateAdScheduleMutation,
  useUpdateAdScheduleMutation,
  useDeleteAdScheduleMutation,
} from '@/lib/query-options';
import type { AdScheduleResponse, AdScheduleCreate, DayOfWeek } from '@/lib/apis';
import { Trash2, Clock, Plus } from 'lucide-react';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import type { ColumnDef } from '@tanstack/react-table';

interface AdScheduleViewProps {
  campaignId: number;
  disabled?: boolean;
}

const DAYS_OF_WEEK: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

interface TimeOption {
  hour: number;
  minute: number;
  label: string;
  value: string;
}

const formatTime = (hour: number, minute: number): string => {
  if (hour === 24) return '23:59';
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

const generateTimeOptions = (): TimeOption[] => {
  const options: TimeOption[] = [];
  for (let hour = 0; hour <= 23; hour++) {
    for (const minute of [0, 15, 30, 45]) {
      options.push({
        hour,
        minute,
        label: formatTime(hour, minute),
        value: `${hour}:${minute}`,
      });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();
const HOUR_OPTIONS = Array.from({ length: 25 }, (_, i) => i);
const MINUTE_OPTIONS = [0, 15, 30, 45];
const formatDayName = (day: string): string => {
  return day.charAt(0) + day.slice(1).toLowerCase();
};


export const AdScheduleView: React.FC<AdScheduleViewProps> = ({ campaignId, disabled = false }) => {
  const { toast } = useToast();
  const { data: schedulesData, isLoading } = useAdSchedulesQuery(campaignId);
  const createMutation = useCreateAdScheduleMutation(campaignId);
  const updateMutation = useUpdateAdScheduleMutation(campaignId);
  const deleteMutation = useDeleteAdScheduleMutation(campaignId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingSchedule, setDeletingSchedule] = useState<AdScheduleResponse | null>(null);
  const [formData, setFormData] = useState<AdScheduleCreate>({
    day_of_week: 'MONDAY',
    start_hour: 9,
    start_minute: 0,
    end_hour: 17,
    end_minute: 0,
    bid_modifier: undefined,
  });

  const schedules = schedulesData?.schedules || [];

  const resetForm = () => {
    setFormData({
      day_of_week: 'MONDAY',
      start_hour: 9,
      start_minute: 0,
      end_hour: 17,
      end_minute: 0,
      bid_modifier: undefined,
    });
    setIsModalOpen(false);
  };

  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      await createMutation.mutateAsync(formData);
      toast({ description: 'Ad schedule created' });
      resetForm();
    } catch (error) {
      toast({
        description: error instanceof Error ? error.message : 'Failed to save ad schedule',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingSchedule) return;
    try {
      await deleteMutation.mutateAsync(deletingSchedule.id);
      toast({ description: 'Ad schedule deleted' });
      setDeletingSchedule(null);
    } catch (error) {
      toast({
        description: error instanceof Error ? error.message : 'Failed to delete ad schedule',
        variant: 'destructive',
      });
    }
  };

  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const handleDebouncedUpdate = (schedule: AdScheduleResponse, updates: Partial<AdScheduleCreate>) => {
    const key = String(schedule.id);
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(() => handleInlineUpdate(schedule, updates), 500);
  };

  const handleInlineUpdate = async (schedule: AdScheduleResponse, updates: Partial<AdScheduleCreate>) => {
    try {
      await updateMutation.mutateAsync({
        scheduleId: schedule.id,
        schedule: {
          day_of_week: schedule.day_of_week as DayOfWeek,
          start_hour: schedule.start_hour,
          start_minute: schedule.start_minute,
          end_hour: schedule.end_hour,
          end_minute: schedule.end_minute,
          bid_modifier: schedule.bid_modifier ?? undefined,
          ...updates,
        },
      });
    } catch (error) {
      toast({
        description: error instanceof Error ? error.message : 'Failed to update ad schedule',
        variant: 'destructive',
      });
    }
  };

  const isSaving = createMutation.isPending;

  const DAY_ORDER: Record<string, number> = {
    MONDAY: 0, TUESDAY: 1, WEDNESDAY: 2, THURSDAY: 3, FRIDAY: 4, SATURDAY: 5, SUNDAY: 6,
  };

  const columns: ColumnDef<AdScheduleResponse, unknown>[] = [
    {
      accessorFn: (row) => DAY_ORDER[row.day_of_week] ?? 0,
      id: 'day',
      header: 'Day',
      meta: { className: 'px-6 w-[30%]' },
      cell: ({ row }) => {
        const schedule = row.original;
        return (
          <div className='flex items-center gap-3'>
            <Clock className='h-4 w-4 text-gray-500 shrink-0' />
            <Select
              value={schedule.day_of_week}
              onValueChange={(value) => handleInlineUpdate(schedule, { day_of_week: value as DayOfWeek })}
              disabled={disabled}
            >
              <SelectTrigger className='w-[130px] h-8 -ml-1.5 text-sm border-transparent shadow-none hover:border-input hover:shadow-sm focus:border-input focus:shadow-sm'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAYS_OF_WEEK.map((day) => (
                  <SelectItem key={day} value={day}>
                    {formatDayName(day)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.start_hour * 60 + row.start_minute,
      id: 'time',
      header: 'Time',
      meta: { className: 'px-6 w-[30%]' },
      cell: ({ row }) => {
        const schedule = row.original;
        return (
          <div className='flex items-center -ml-2'>
            <Select
              value={schedule.start_hour.toString()}
              onValueChange={(value) => handleInlineUpdate(schedule, { start_hour: parseInt(value) })}
              disabled={disabled}
            >
              <SelectTrigger className='w-[62px] h-8 px-2 text-sm border-transparent shadow-none hover:border-input hover:shadow-sm'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOUR_OPTIONS.slice(0, 24).map((hour) => (
                  <SelectItem key={hour} value={hour.toString()}>
                    {hour.toString().padStart(2, '0')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className='text-sm text-gray-400 -mx-0.5'>:</span>
            <Select
              value={schedule.start_minute.toString()}
              onValueChange={(value) => handleInlineUpdate(schedule, { start_minute: parseInt(value) })}
              disabled={disabled}
            >
              <SelectTrigger className='w-[62px] h-8 px-2 text-sm border-transparent shadow-none hover:border-input hover:shadow-sm'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MINUTE_OPTIONS.map((minute) => (
                  <SelectItem key={minute} value={minute.toString()}>
                    {minute.toString().padStart(2, '0')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className='text-sm text-gray-400 mx-1'>–</span>
            <Select
              value={schedule.end_hour.toString()}
              onValueChange={(value) => handleInlineUpdate(schedule, { end_hour: parseInt(value) })}
              disabled={disabled}
            >
              <SelectTrigger className='w-[62px] h-8 px-2 text-sm border-transparent shadow-none hover:border-input hover:shadow-sm'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOUR_OPTIONS.map((hour) => (
                  <SelectItem key={hour} value={hour.toString()}>
                    {hour.toString().padStart(2, '0')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className='text-sm text-gray-400 -mx-0.5'>:</span>
            <Select
              value={schedule.end_minute.toString()}
              onValueChange={(value) => handleInlineUpdate(schedule, { end_minute: parseInt(value) })}
              disabled={disabled}
            >
              <SelectTrigger className='w-[62px] h-8 px-2 text-sm border-transparent shadow-none hover:border-input hover:shadow-sm'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MINUTE_OPTIONS.map((minute) => (
                  <SelectItem key={minute} value={minute.toString()}>
                    {minute.toString().padStart(2, '0')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      },
    },
    {
      accessorKey: 'bid_modifier',
      header: 'Bid Modifier',
      meta: { className: 'px-6 w-[30%]' },
      cell: ({ row }) => {
        const schedule = row.original;
        return (
          <Input
            type='number'
            step='0.1'
            min='0.1'
            max='10.0'
            value={schedule.bid_modifier ?? ''}
            onChange={(e) => {
              const val = e.target.value ? parseFloat(e.target.value) : undefined;
              handleDebouncedUpdate(schedule, { bid_modifier: val });
            }}
            placeholder='—'
            disabled={disabled}
            className='w-[90px] h-8 -ml-3 text-sm border-transparent shadow-none hover:border-input hover:shadow-sm focus:border-input focus:shadow-sm'
          />
        );

      },
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className='flex justify-end'>
          <IconButton
            variant='destructive'
            aria-label='Delete'
            onClick={() => setDeletingSchedule(row.original)}
            disabled={disabled}
          >
            <Trash2 className='h-4 w-4' />
          </IconButton>
        </div>
      ),
      meta: { className: 'px-6 text-right' },
    },
  ];

  if (isLoading) {
    return (
      <div>
        <h2 className='text-xl font-semibold text-gray-900'>Scheduling</h2>
        <p className='text-sm text-gray-600 mt-1'>Loading ad schedules...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className='flex items-center justify-between mb-4'>
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>Scheduling</h2>
          <p className='text-sm text-gray-600 mt-1'>Control when your ads are shown.</p>
        </div>
        <Button
          variant='outline'
          className='text-gray-900'
          onClick={openAddModal}
          disabled={disabled}
        >
          <Plus className='h-4 w-4 mr-2' />
          Add Schedule
        </Button>
      </div>

      {/* Table */}
      <DataTable
        data={schedules}
        columns={columns}
        pageSize={999}
        emptyIcon={Clock}
        emptyTitle="No Schedulings"
        emptyDescription="Your ads are currently running 24/7. Add a schedule to limit when your ads are shown."
      />

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='sm:max-w-[500px]' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Scheduling</DialogTitle>
          </DialogHeader>

          <div className='py-4 h-[320px] space-y-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label>Day of Week</Label>
                <Select
                  value={formData.day_of_week}
                  onValueChange={(value) => setFormData({ ...formData, day_of_week: value as DayOfWeek })}
                  disabled={disabled}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((day) => (
                      <SelectItem key={day} value={day}>
                        {formatDayName(day)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-2'>
                <Label>Bid Modifier (Optional)</Label>
                <Input
                  type='number'
                  step='0.1'
                  min='0.1'
                  max='10.0'
                  value={formData.bid_modifier || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      bid_modifier: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                  placeholder='1.0 (no change)'
                  disabled={disabled}
                />
                <p className='text-xs text-gray-500'>1.0 = no change, 1.5 = +50%, 0.5 = -50%</p>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label>Start Time</Label>
                <Select
                  value={`${formData.start_hour}:${formData.start_minute}`}
                  onValueChange={(value) => {
                    const [hour, minute] = value.split(':').map(Number);
                    setFormData({ ...formData, start_hour: hour, start_minute: minute });
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-2'>
                <Label>End Time</Label>
                <Select
                  value={`${formData.end_hour}:${formData.end_minute}`}
                  onValueChange={(value) => {
                    const [hour, minute] = value.split(':').map(Number);
                    setFormData({ ...formData, end_hour: hour, end_minute: minute });
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className='flex justify-end gap-2'>
            <Button variant='outline' onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={disabled || isSaving}
              className='bg-[#290BD9] hover:bg-[#2309C5]'
            >
              {isSaving ? 'Saving...' : 'Add'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConformationModal
        isOpen={!!deletingSchedule}
        onClose={() => setDeletingSchedule(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Scheduling"
        description={`Are you sure you want to delete the ${deletingSchedule ? formatDayName(deletingSchedule.day_of_week) : ''} schedule? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
